/**
 * Turn a report's column catalog into the shape the scheduler's column picker
 * renders.
 *
 * The catalogs are NOT uniform, which is why this exists:
 *
 *  - Labor Management hands over its full flat column list — wages,
 *    budgetWages, wageVar, hours, budgetHours, hoursVar … — where each entry
 *    carries `variant: 'budget' | 'variance'` (or nothing, meaning actual).
 *    Those genuinely form a metric × facet grid, so the picker draws one.
 *  - The Income Statement hands over its ALREADY-COLLAPSED facet items
 *    (useColumnPrefs buildFacetModel groups month × metric and gives back one
 *    control per metric). That is four checkboxes, not a grid.
 *  - Reviews, GL Detail and the rest are plain lists with no facets at all.
 *
 * So: group into a grid only when the catalog supports it, and fall back to a
 * flat checklist otherwise. Either way the keys emitted are the catalog's own,
 * untouched — applyPresentation on the server matches on those exact strings,
 * so a wrong grouping could only ever look odd, never select the wrong column.
 */

export const FACETS = ['actual', 'budget', 'variance', 'forecast'];
export const FACET_LABELS = {
    actual: 'Actual',
    budget: 'Budget',
    variance: 'Variance',
    forecast: 'Forecast',
};

const lowerFirst = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);

/** The facet a catalog entry belongs to, from its declared variant. */
function facetOf(column) {
    const variant = column.variant || column.facet;
    if (variant === 'budget' || variant === 'variance' || variant === 'forecast') return variant;
    return 'actual';
}

/**
 * The actual-facet key a budget/variance column belongs with.
 * budgetWages → wages · wageVar → wage → wages (singular/plural tolerated).
 */
function stemFor(key, facet, actualKeys) {
    if (facet === 'budget' && /^budget/i.test(key)) {
        const stripped = lowerFirst(key.replace(/^budget/i, ''));
        if (actualKeys.has(stripped)) return stripped;
        if (actualKeys.has(`${stripped}s`)) return `${stripped}s`;
    }
    if (facet === 'variance' && /Var$/.test(key)) {
        const stripped = key.replace(/Var$/, '');
        if (actualKeys.has(stripped)) return stripped;
        if (actualKeys.has(`${stripped}s`)) return `${stripped}s`;
    }
    if (facet === 'forecast' && /^forecast/i.test(key)) {
        const stripped = lowerFirst(key.replace(/^forecast/i, ''));
        if (actualKeys.has(stripped)) return stripped;
    }
    return null;
}

/**
 * @param {Array<{key, label, description, variant?, facet?}>} columns
 * @returns {{ mode: 'grid'|'list', facets: string[], metrics: Array, columns: Array }}
 *   grid → metrics[] of { key, label, cells: { [facet]: columnKey|null } }
 *   list → columns[] as given
 */
export function buildColumnModel(columns) {
    const catalog = (columns || []).filter((c) => c && c.key);
    const flat = { mode: 'list', facets: [], metrics: [], columns: catalog };
    if (catalog.length < 4) return flat;

    const actualKeys = new Set(catalog.filter((c) => facetOf(c) === 'actual').map((c) => c.key));
    if (!actualKeys.size) return flat;

    const metrics = new Map();
    const orphans = [];
    catalog.forEach((column) => {
        const facet = facetOf(column);
        if (facet === 'actual') {
            metrics.set(column.key, {
                key: column.key,
                label: column.label,
                description: column.description || null,
                cells: { actual: column.key },
            });
        }
    });
    catalog.forEach((column) => {
        const facet = facetOf(column);
        if (facet === 'actual') return;
        const stem = stemFor(column.key, facet, actualKeys);
        if (stem && metrics.has(stem) && !metrics.get(stem).cells[facet]) {
            metrics.get(stem).cells[facet] = column.key;
        } else {
            orphans.push(column);
        }
    });

    // A grid only earns its place when at least two facets are populated and
    // most of the catalog actually fits the grid. Otherwise the flat list is
    // both simpler and more honest.
    const used = FACETS.filter((f) => [...metrics.values()].some((m) => m.cells[f]));
    const placed = catalog.length - orphans.length;
    if (used.length < 2 || placed < catalog.length * 0.7) return flat;

    // orphans keep their own actual-only row so nothing disappears
    orphans.forEach((column) => {
        metrics.set(column.key, {
            key: column.key,
            label: column.label,
            description: column.description || null,
            cells: { actual: column.key },
        });
    });

    return {
        mode: 'grid',
        facets: used,
        metrics: [...metrics.values()],
        columns: catalog,
    };
}

/** Every column key the model can produce, in catalog order. */
export function modelKeys(model) {
    if (model.mode === 'list') return model.columns.map((c) => c.key);
    const seen = new Set();
    model.columns.forEach((c) => seen.add(c.key));
    return [...seen];
}

/**
 * Toggling a grid cell adds or removes ONE catalog key, keeping the result in
 * catalog order so the printed table matches the live report.
 */
export function toggleKey(selected, key, catalogOrder) {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return catalogOrder.filter((k) => next.has(k));
}

/** Toggle a whole facet column at once. */
export function toggleFacetColumn(selected, model, facet, catalogOrder) {
    const keys = model.metrics.map((m) => m.cells[facet]).filter(Boolean);
    const allOn = keys.every((k) => selected.includes(k));
    const next = new Set(selected);
    keys.forEach((k) => (allOn ? next.delete(k) : next.add(k)));
    return catalogOrder.filter((k) => next.has(k));
}
